// ─── Реальный прайс PLANIUM ───────────────────────────────────────────────
// Любая правка цен — только здесь.

export const MIN_CALLOUT_PRICE = 7000;          // фолбэк, если сервер недоступен
export const BASE_RATE = 150;                    // фолбэк ₽ за м² (было 120)

// Скидки по площади как доля от базовой ставки (порог м² → множитель)
export const AREA_DISCOUNTS = [
  { threshold: 1000, factor: 0.65, label: "−35% свыше 1000 м²" },
  { threshold: 500,  factor: 0.75, label: "−25% свыше 500 м²" },
  { threshold: 150,  factor: 0.90, label: "−10% свыше 150 м²" },
  { threshold: 0,    factor: 1.00, label: "Базовая ставка" },
];

export const OUT_OF_CITY_PER_KM = 50;            // ₽ за км от центра города
export const ELECTRICITY_PER_SQM = 25;           // ₽ за м² замера электрики
export const OUTSIDE_FACADE_PER_10M = 2500;      // ₽ за 10 м уличного фасада
export const INSIDE_FACADE_PER_10M = 2000;       // ₽ за 10 м внутреннего фасада
export const URGENT_FLAT = 3000;                 // срочность за 1 сутки
export const AUDIT_PER_100_SQM = 3500;           // аудит за 100 м²
export const KEY_DELIVERY_FLAT = 3000;           // доставка ключей
export const WINTER_MULTIPLIER = 1.2;            // зимний сезон без отопления

export const OBJECT_TYPES = [
  "Квартира", "Офис", "Коммерческое", "Частный дом",
  "Склад", "Производственное", "Другое",
];

export const PROGRAMS = [
  "AutoCAD", "ArchiCAD", "Revit", "nanoCAD", "FreeCAD", "Другое",
];

// ─── Формула расчёта ──────────────────────────────────────────────────────
// params: { area, outOfCityKm, electricity, outsideFacadeM, insideFacadeM,
//           urgent, auditArea, keyDelivery, winter }
// Возвращает { total, breakdown[], days, rate }
export function calculatePrice(params) {
  const {
    area = 80,
    outOfCityKm = 0,
    electricity = false,
    outsideFacadeM = 0,
    insideFacadeM = 0,
    urgent = false,
    auditArea = 0,
    keyDelivery = false,
    winter = false,
    baseRate = BASE_RATE,
    minCallout = MIN_CALLOUT_PRICE,
  } = params;

  const breakdown = [];

  // 1. Базовая ставка по площади со скидкой
  const tier = AREA_DISCOUNTS.find(t => area > t.threshold) || AREA_DISCOUNTS[AREA_DISCOUNTS.length - 1];
  const rate = Math.round(baseRate * tier.factor);
  const baseCost = Math.round(area * rate);
  breakdown.push({
    label: `Замер ${area} м² × ${rate} ₽`,
    hint: tier.threshold > 0 ? tier.label : null,
    amount: baseCost,
  });

  // 2. Выезд за город
  if (outOfCityKm > 0) {
    const travel = outOfCityKm * OUT_OF_CITY_PER_KM;
    breakdown.push({
      label: `Выезд за город ${outOfCityKm} км × 50 ₽`,
      amount: travel,
    });
  }

  // 3. Электрика
  if (electricity) {
    const el = Math.round(area * ELECTRICITY_PER_SQM);
    breakdown.push({ label: `Замер электрики, ${area} м² × 25 ₽`, amount: el });
  }

  // 4. Уличный фасад
  if (outsideFacadeM > 0) {
    const segments = Math.ceil(outsideFacadeM / 10);
    const cost = segments * OUTSIDE_FACADE_PER_10M;
    breakdown.push({
      label: `Уличный фасад ${outsideFacadeM} м (${segments} × 10 м)`,
      amount: cost,
    });
  }

  // 5. Внутренний фасад
  if (insideFacadeM > 0) {
    const segments = Math.ceil(insideFacadeM / 10);
    const cost = segments * INSIDE_FACADE_PER_10M;
    breakdown.push({
      label: `Внутренний фасад ${insideFacadeM} м (${segments} × 10 м)`,
      amount: cost,
    });
  }

  // 6. Срочность
  if (urgent) {
    breakdown.push({ label: "Срочный выезд (1 сутки)", amount: URGENT_FLAT });
  }

  // 7. Аудит
  if (auditArea > 0) {
    const hundreds = Math.ceil(auditArea / 100);
    breakdown.push({
      label: `Аудит объекта ${auditArea} м² (${hundreds} × 100 м²)`,
      amount: hundreds * AUDIT_PER_100_SQM,
    });
  }

  // 8. Ключи
  if (keyDelivery) {
    breakdown.push({ label: "Доставка ключей (забрать/вернуть)", amount: KEY_DELIVERY_FLAT });
  }

  // Промежуточный итог
  let subtotal = breakdown.reduce((sum, item) => sum + item.amount, 0);

  // 9. Зимний коэффициент (×1.2 к итогу)
  let winterExtra = 0;
  if (winter) {
    winterExtra = Math.round(subtotal * (WINTER_MULTIPLIER - 1));
    breakdown.push({
      label: "Зимний сезон (без отопления) +20%",
      amount: winterExtra,
    });
    subtotal += winterExtra;
  }

  // 10. Минимальная стоимость выезда
  const total = Math.max(subtotal, minCallout);
  const minApplied = total === minCallout && subtotal < minCallout;

  // 11. Срок исполнения
  const days = urgent ? 1 : area < 100 ? 2 : area < 300 ? 3 : area < 800 ? 4 : 5;

  return { total, breakdown, days, rate, minApplied };
}